import { computed, onMounted, reactive, ref, watch, type ComputedRef, type Ref } from 'vue';
import {
  isNavigationFailure,
  useRoute,
  useRouter,
  type LocationQueryRaw,
  type LocationQueryValue,
} from 'vue-router';
import {
  getDocuments,
  getFields,
  getParticipants,
  getVaultFileMetadata,
  PreprocessingTaskStatus,
  SignatureFieldType,
  SignatureType,
  SigningFileType,
  SigningRequestSigneeRole,
  SigningRequestStatus,
  type ContractManagementContractFileInDb,
  type ParticipantCreate,
  type ParticipantInDb,
  SigningProvider,
  VaultFileRevisionKind,
  type VaultFileRevisionInDb,
} from '@/api';
import { SignatureRequestService } from '@/services/contractManagement/signatureRequestService';
import { ContractFileDetailService } from '@/services/contractManagement/contractFileDetailService';
import { ContractFilesService } from '@/services/contractManagement/contractFilesService';
import { ContractsService } from '@/services/contractManagement/contractsService';
import {
  activateSignatureDraft,
  createSignatureDraft,
  draftSaveKey,
  saveSignatureDraft,
} from '@/services/integration/youtrustSignatureService';
import type { GaiaSaveStateValue } from '@/components/design/composite/GaiaSaveState.vue';
import { isEnumValue } from '@/utils/enumUtils';
import { logger } from '@/utils/logger';
import type {
  SignatureActivateForm,
  SignatureActivateRequest,
  SignatureActivateResponse,
  SignatureAddSigneesRequest,
  SignatureAddSigneesResponse,
  SignatureDraftField,
  SignatureFlowStepKey,
  SignatureInitializeForm,
  SignatureInitializeRequest,
  SignatureInitializeResponse,
  SignatureReviewForm,
  SignatureReviewParty,
  SignatureReviewResponse,
  SignatureSigneeForm,
  SignatureStepState,
  SignatureSupersededDocument,
  SignatureUploadContractSelectionState,
  SignatureUploadDocumentForm,
  SignatureUploadForm,
  SignatureUploadRequest,
  SignatureUploadResponse,
} from '@/types/contractManagement/signatureFlow';
import { useOrganizationStore } from '@/stores/organizationStore';
import { useTourStore } from '@/stores/tourStore';
import { useVaultWebSocket } from '@/composables/useVaultWebSocket';
import { extractErrorDetail } from '@/utils/errorHandler';
import { ACCESS_PERMISSION_WRITE, allows } from '@/utils/accessPermission';
import { buildEffectiveFileFromRevision } from '@/utils/signatureFileValidation';
import { isSignatureParticipantNameValid } from '@/utils/signatureParticipantNameValidation';
import { useGaiaToast } from '@/composables/useGaiaToast';
import { contractRoute, signingRequestEditRoute, signingRequestRoute } from '@/router/links';

/**
 * The prepare-signature state machine, lifted out of the view so the rebuilt
 * wizard (`views/signing/SigningWizardView.vue`) is only layout and panes.
 *
 * The rules here are the ones the previous prepare-signature view enforced —
 * nothing about *what* makes a step complete changed, only where it lives.
 * Every gate also exposes a sentence saying what is missing, which the UI
 * shows as a blocker.
 *
 * Work in progress lives on the server, like a contract-generation draft: the
 * request is created as a `draft` with the first document, autosaved as it is
 * edited, and reopened from the signature requests list through the edit route.
 */

/** Pause after the last edit before it is sent — field dragging is many edits. */
const AUTOSAVE_DEBOUNCE_MS = 1000;
/** Left in browsers by the retired local draft cache; removed on sight. */
const LEGACY_DRAFT_KEY_PREFIX = 'signature-flow-draft-';
const FIXED_SIGNATURE_DELIVERY_MODE = 'email';
// Mirrors the backend's `contains_email`: a run of non-separator chars around an
// `@`, with a dotted domain — loose enough for "max@gaia.law" inside a sentence.
const EMAIL_IN_TEXT_PATTERN = /[^\s()<>[\]{}"',;:@]+@[^\s()<>[\]{}"',;:@]+\.[a-z]{2,}/i;

export interface SigningWizardStepDefinition {
  key: SignatureFlowStepKey;
  number: number;
  /** Rail label. */
  title: string;
  /** One line under the rail entry and under the panel heading. */
  hint: string;
  /** Panel heading for the step's body. */
  heading: string;
}

export const SIGNING_WIZARD_STEPS: SigningWizardStepDefinition[] = [
  // The documents come first and the request is named after them, so naming
  // it is part of this step rather than one of its own. `initialize` stays a
  // key of the state (its form, its gate, saved drafts) but is not a step on
  // the rail: `upload` gates on both.
  {
    key: 'upload',
    number: 1,
    title: 'Documents',
    hint: 'Pick what is signed — the request is named after it.',
    heading: 'Choose the documents',
  },
  {
    key: 'signees',
    number: 2,
    title: 'Recipients',
    hint: 'Pick signers, approvers and followers from your entities.',
    heading: 'Add the recipients',
  },
  {
    key: 'review',
    number: 3,
    title: 'Signature fields',
    hint: 'Place the fields each signer must fill.',
    heading: 'Place the signature fields',
  },
  {
    key: 'activate',
    number: 4,
    title: 'Review & send',
    hint: 'Check the signature and send it out.',
    heading: 'Review & send',
  },
];

interface WizardState {
  signingRequestUid: string | null;
  initializeForm: SignatureInitializeForm;
  uploadForm: SignatureUploadForm;
  signeesForm: SignatureSigneeForm[];
  reviewForm: SignatureReviewForm;
  activateForm: SignatureActivateForm;
  steps: {
    initialize: SignatureStepState<SignatureInitializeResponse>;
    upload: SignatureStepState<SignatureUploadResponse>;
    signees: SignatureStepState<SignatureAddSigneesResponse>;
    review: SignatureStepState<SignatureReviewResponse>;
    activate: SignatureStepState<SignatureActivateResponse>;
  };
}

type QueryPrefillMode = 'contract' | 'contract-files' | 'vault-files' | null;

interface SigningRequestContractContext {
  contract_uid?: string | null;
  contract_file_uid?: string | null;
}

/** `<input type="datetime-local">` / n-date-picker round-trip format. */
export function toDatetimeLocal(value: Date): string {
  const year = value.getFullYear();
  const month = `${value.getMonth() + 1}`.padStart(2, '0');
  const day = `${value.getDate()}`.padStart(2, '0');
  const hours = `${value.getHours()}`.padStart(2, '0');
  const minutes = `${value.getMinutes()}`.padStart(2, '0');

  return `${year}-${month}-${day}T${hours}:${minutes}`;
}

/*
 * An expiry is a calendar day: Yousign keeps only the date (the backend sends
 * `.date()` of the UTC value). The form holds the day at 23:59 local, so today
 * still counts as in the future; it is sent as 23:59 UTC of that same day, so
 * no timezone moves it to the day before.
 */

/** The end (23:59) of the local day `timestamp` falls on. */
export function endOfLocalDay(timestamp: number): Date {
  const day = new Date(timestamp);
  day.setHours(23, 59, 0, 0);
  return day;
}

/**
 * Yousign refuses an expiry more than a year from the moment the request is sent.
 * The expiry is the *end* of its day, so "365 days from today" is always a little
 * over a year — the last day safely inside is 364 days out (a year is ≥ 365 days).
 */
export const MAX_EXPIRY_DAYS_AHEAD = 364;
const DAY_MS = 24 * 60 * 60 * 1000;

/** Start (local midnight) of the last day a request may expire on, counted from today. */
export function latestExpiryDay(): number {
  const day = new Date();
  day.setHours(0, 0, 0, 0);
  day.setDate(day.getDate() + MAX_EXPIRY_DAYS_AHEAD);
  return day.getTime();
}

/** Days a date picker greys out: the past, and anything the provider would refuse as too far. */
export function isUnavailableExpiryDay(timestamp: number): boolean {
  return timestamp < new Date().setHours(0, 0, 0, 0) || timestamp >= latestExpiryDay() + DAY_MS;
}

/** Start of the day `days` from today — a default expiry, held to the provider's one-year limit. */
export function expiryDayInDays(days: number): number {
  const day = new Date();
  day.setHours(0, 0, 0, 0);
  day.setDate(day.getDate() + days);
  return Math.min(day.getTime(), latestExpiryDay());
}

/** The local day `timestamp` falls on, as the backend's expiry: 23:59 UTC that day. */
export function expiryDayToIso(timestamp: number): string {
  const day = new Date(timestamp);
  return new Date(Date.UTC(day.getFullYear(), day.getMonth(), day.getDate(), 23, 59)).toISOString();
}

/** A stored expiry for reading: its day only ("20 Nov 2026"), taken in UTC like the form does. */
export function formatExpiryDay(iso: string | null | undefined): string {
  if (!iso) return 'No expiry set';
  return new Date(iso).toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    timeZone: 'UTC',
  });
}

/** A stored expiry's day (its UTC date), as the form's local end-of-day string. */
export function expiryDayFromIso(iso: string): string {
  const stored = new Date(iso);
  return toDatetimeLocal(
    new Date(stored.getUTCFullYear(), stored.getUTCMonth(), stored.getUTCDate(), 23, 59)
  );
}

export interface UseSigningWizardReturn {
  wizard: WizardState;
  activeStep: Ref<SignatureFlowStepKey>;
  activeStepDefinition: ComputedRef<SigningWizardStepDefinition>;
  isEditMode: ComputedRef<boolean>;
  contractUid: ComputedRef<string | null>;
  reviewParties: ComputedRef<SignatureReviewParty[]>;
  activateRequestPreview: ComputedRef<SignatureActivateRequest>;
  signatureClearAcknowledgements: Ref<Record<string, boolean>>;
  revisionsRequiringClearAck: ComputedRef<VaultFileRevisionInDb[]>;
  /** Documents whose file gained a newer revision after fields were placed. */
  documentsWithSupersededRevision: ComputedRef<SignatureSupersededDocument[]>;
  /** Re-pin a document to its parked newer revision and reopen field placement. */
  adoptDocumentRevision: (vaultFileUid: string) => void;
  allSignableDocumentsDetected: ComputedRef<boolean>;
  signableUploadDocuments: ComputedRef<SignatureUploadDocumentForm[]>;
  initializeNameError: ComputedRef<string | null>;
  initializeExpirationError: ComputedRef<string | null>;
  /** The server draft's autosave state, for the footer indicator. */
  saveState: Ref<GaiaSaveStateValue>;
  /** Why the last save failed, while it is failing. */
  saveError: Ref<string | null>;
  lastSavedAt: Ref<Date | null>;
  /** Send pending changes now — "Save now", and the leave guard. */
  flushSaves: () => Promise<void>;
  submitting: ComputedRef<boolean>;
  continueLabel: ComputedRef<string>;
  continueDisabled: ComputedRef<boolean>;
  canGoBack: ComputedRef<boolean>;
  /** Why the active step can't be left yet, or null when it can. */
  activeStepBlocker: ComputedRef<string | null>;
  isStepAccessible: (stepKey: SignatureFlowStepKey) => boolean;
  isStepComplete: (stepKey: SignatureFlowStepKey) => boolean;
  stepCount: (stepKey: SignatureFlowStepKey) => number | undefined;
  selectStep: (stepKey: SignatureFlowStepKey) => void;
  goBack: () => void;
  goNext: () => Promise<void>;
  navigateBack: () => void;
  updateInitializeForm: (value: SignatureInitializeForm) => void;
  updateUploadForm: (value: SignatureUploadForm) => void;
  updateSigneesForm: (value: SignatureSigneeForm[]) => void;
  updateReviewForm: (value: SignatureReviewForm) => void;
}

export function useSigningWizard(): UseSigningWizardReturn {
  const route = useRoute();
  const router = useRouter();
  const message = useGaiaToast();
  const organizationStore = useOrganizationStore();

  const activeStep = ref<SignatureFlowStepKey>('upload');
  /** Off while the wizard is being filled from a load or a prefill — those aren't edits. */
  const persistenceReady = ref(false);
  const defaultInitializeForm = createDefaultInitializeForm();
  const signatureClearAcknowledgements = ref<Record<string, boolean>>({});

  const wizard = reactive<WizardState>({
    signingRequestUid: null,
    initializeForm: { ...defaultInitializeForm },
    uploadForm: { documents: [], contract_selection: null },
    signeesForm: [],
    reviewForm: createDefaultReviewForm(),
    activateForm: { confirmed: false },
    steps: {
      initialize: createStepState<SignatureInitializeResponse>(),
      upload: createStepState<SignatureUploadResponse>(),
      signees: createStepState<SignatureAddSigneesResponse>(),
      review: createStepState<SignatureReviewResponse>(),
      activate: createStepState<SignatureActivateResponse>(),
    },
  });

  // ── Route context ─────────────────────────────────────────────────────────

  // Read once: the first save moves a new request onto its edit route, which has
  // no contract in the URL, and the wizard must keep the contract it opened with.
  const openedForContractUid = typeof route.params.uid === 'string' ? route.params.uid : null;
  const contractUid = computed(() => openedForContractUid);

  /**
   * Set only on the edit route, where the wizard reworks an existing draft
   * (e.g. one a playbook's signature-preparation action created).
   */
  const editingSigningRequestUid = computed(() =>
    typeof route.params.signingRequestUid === 'string' && route.params.signingRequestUid.length > 0
      ? route.params.signingRequestUid
      : null
  );
  const isEditMode = computed(() => editingSigningRequestUid.value !== null);
  const launchedFromOverview = computed(() => route.query.from === 'overview');

  const prefillContractUid = computed(() =>
    firstQueryValue(route.query.contract_uid, route.query['contract_uid[]'])
  );
  const prefillContractFileUids = computed(() =>
    queryListValue(
      route.query.contract_file_uid,
      route.query.contract_file_uids,
      route.query['contract_file_uid[]'],
      route.query['contract_file_uids[]']
    )
  );
  const prefillVaultFileUids = computed(() =>
    queryListValue(route.query.vault_file_uids, route.query['vault_file_uids[]'])
  );

  const prefillMode = computed<QueryPrefillMode>(() => {
    if (prefillContractUid.value) return 'contract';
    if (prefillContractFileUids.value.length > 0) return 'contract-files';
    if (prefillVaultFileUids.value.length > 0) return 'vault-files';
    return null;
  });

  // ── Payload previews ──────────────────────────────────────────────────────

  const signingRequestContractContext = computed<SigningRequestContractContext>(() => {
    const selectedContract = wizard.uploadForm.contract_selection;
    const selectedContractFileUid = singleUid(selectedContract?.contract_file_uids ?? []);
    const routeContractFileUid = singleUid(prefillContractFileUids.value);

    return {
      contract_uid: selectedContract?.contract_uid ?? prefillContractUid.value ?? contractUid.value,
      contract_file_uid: selectedContractFileUid ?? routeContractFileUid,
    };
  });

  const initializeRequestPreview = computed<SignatureInitializeRequest>(() =>
    buildInitializePayload(wizard.initializeForm, signingRequestContractContext.value)
  );
  const uploadRequestPreview = computed<SignatureUploadRequest>(() =>
    buildUploadPayload(wizard.uploadForm)
  );
  const normalizedSignees = computed(() => buildSigneesPayload(wizard.signeesForm));
  const reviewParties = computed<SignatureReviewParty[]>(() =>
    buildReviewParties(wizard.signeesForm)
  );
  const activateRequestPreview = computed<SignatureActivateRequest>(() => ({
    initialize: initializeRequestPreview.value,
    documents: uploadRequestPreview.value,
    participants: normalizedSignees.value,
    fields: wizard.reviewForm.fields,
  }));

  const isAnyStepLoading = computed(() => Object.values(wizard.steps).some(step => step.loading));
  const submitting = computed(() => wizard.steps.activate.loading);

  // ── Gates ─────────────────────────────────────────────────────────────────

  /**
   * Youtrust rejects names that look like HTML tags or contain an email address,
   * so both are refused up front (the backend enforces the email rule too).
   */
  const initializeNameError = computed<string | null>(() => {
    const name = wizard.initializeForm.name;
    if (/[<>]/.test(name)) {
      return 'Remove "<" and ">" from the name — the signing provider doesn\'t allow them.';
    }
    if (EMAIL_IN_TEXT_PATTERN.test(name)) {
      return "Remove the email address from the name — the signing provider doesn't allow it.";
    }
    return null;
  });

  const initializeExpirationError = computed<string | null>(() => {
    const raw = wizard.initializeForm.expiration_date;
    if (!raw) return null;

    const expirationDate = new Date(raw);
    if (Number.isNaN(expirationDate.getTime())) return 'Enter a valid expiration date.';
    if (expirationDate.getTime() <= Date.now()) return 'Expiration date must be in the future.';
    // A draft can be reopened long after its expiry was picked; the limit counts from now.
    if (expirationDate.getTime() >= latestExpiryDay() + DAY_MS) {
      const latest = new Date(latestExpiryDay()).toLocaleDateString('en-GB', {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
      });
      return `The signing provider allows an expiry at most a year away — pick ${latest} or earlier.`;
    }
    return null;
  });

  const hasInitializeConfiguration = computed(
    () =>
      Boolean(wizard.initializeForm.provider?.trim()) &&
      initializeExpirationError.value === null &&
      initializeNameError.value === null &&
      wizard.initializeForm.name.trim().length > 0
  );

  const hasUploadConfiguration = computed(() => wizard.uploadForm.documents.length > 0);

  const hasSigneesConfiguration = computed(
    () =>
      normalizedSignees.value.participants.length > 0 &&
      wizard.signeesForm.every(signee => isSigneeReadyForSubmission(signee))
  );

  const signableUploadDocuments = computed(() =>
    wizard.uploadForm.documents.filter(
      document => document.signing_file_type === SigningFileType.SIGNABLE_DOCUMENT
    )
  );

  const signerPartiesWithoutSignatureField = computed(() => {
    const withField = new Set(
      wizard.reviewForm.fields
        .filter(field => field.type === SignatureFieldType.SIGNATURE)
        .map(field => field.recipient_id)
        .filter((uid): uid is string => typeof uid === 'string' && uid.length > 0)
    );

    return reviewParties.value.filter(
      party => party.role === SigningRequestSigneeRole.SIGNER && !withField.has(party.uid)
    );
  });

  const allReviewSignersHaveSignatureField = computed(
    () =>
      reviewParties.value.some(party => party.role === SigningRequestSigneeRole.SIGNER) &&
      signerPartiesWithoutSignatureField.value.length === 0
  );

  const signableDocumentsWithoutSignatureField = computed(() => {
    const withField = new Set(
      wizard.reviewForm.fields
        .filter(field => field.type === SignatureFieldType.SIGNATURE)
        .map(field => field.vault_file_revision_uid)
        .filter((uid): uid is string => typeof uid === 'string' && uid.length > 0)
    );

    return signableUploadDocuments.value.filter(document => {
      const revisionUid = document.vault_file.current_revision?.uid;
      return typeof revisionUid !== 'string' || !withField.has(revisionUid);
    });
  });

  const allReviewSignableDocumentsHaveSignatureField = computed(
    () =>
      signableUploadDocuments.value.length > 0 &&
      signableDocumentsWithoutSignatureField.value.length === 0
  );

  const signableDocumentRevisions = computed(() =>
    signableUploadDocuments.value
      .map(document => document.vault_file.current_revision)
      .filter((revision): revision is VaultFileRevisionInDb => revision != null)
  );

  const allSignableDocumentsDetected = computed(() =>
    signableDocumentRevisions.value.every(revision => {
      const status = revision.signature_detection_status;
      return status === PreprocessingTaskStatus.DONE || status === PreprocessingTaskStatus.FAILED;
    })
  );

  /**
   * Revisions that already carry a signature (or whose detection failed):
   * activating clears those signatures, so each needs an explicit tick.
   */
  const revisionsRequiringClearAck = computed(() =>
    signableDocumentRevisions.value.filter(revision => {
      if (revision.signature_detection_status === PreprocessingTaskStatus.FAILED) {
        return true;
      }

      return (
        revision.signature_detection_status === PreprocessingTaskStatus.DONE &&
        (revision.signature_infos?.length ?? 0) > 0
      );
    })
  );

  const allRequiredSignatureClearAcksGiven = computed(() =>
    revisionsRequiringClearAck.value.every(
      revision => signatureClearAcknowledgements.value[revision.uid]
    )
  );

  // A signable document's file gained a newer revision while this request was
  // being prepared. The wizard never adopts it on its own — the placed fields
  // belong to the pinned revision — so it is parked here until the user
  // explicitly switches over on the activate step.
  const supersededRevisions = ref<Record<string, VaultFileRevisionInDb | undefined>>({});

  const documentsWithSupersededRevision = computed<SignatureSupersededDocument[]>(() =>
    signableUploadDocuments.value.flatMap(document => {
      const newRevision = supersededRevisions.value[document.vault_file.uid];
      const pinnedRevision = document.vault_file.current_revision;
      if (!newRevision || !pinnedRevision) return [];

      return {
        vault_file_uid: document.vault_file.uid,
        pinned_revision: pinnedRevision,
        new_revision: newRevision,
      };
    })
  );

  // Every field must name the revision its document is pinned to, or the
  // provider receives a document the fields do not belong to and rejects the
  // signer as field-less. Placement keeps this true; the check is the backstop
  // for a request that was loaded already divergent.
  const fieldsOnUnpinnedRevision = computed(() => {
    const pinnedRevisionUids = new Set(
      signableDocumentRevisions.value.map(revision => revision.uid)
    );

    return wizard.reviewForm.fields.filter(
      field =>
        !field.vault_file_revision_uid || !pinnedRevisionUids.has(field.vault_file_revision_uid)
    );
  });

  const canActivate = computed(
    () =>
      hasInitializeConfiguration.value &&
      hasUploadConfiguration.value &&
      hasSigneesConfiguration.value &&
      wizard.steps.review.completed &&
      !isAnyStepLoading.value &&
      allSignableDocumentsDetected.value &&
      allRequiredSignatureClearAcksGiven.value &&
      documentsWithSupersededRevision.value.length === 0 &&
      fieldsOnUnpinnedRevision.value.length === 0
  );

  /** What still stops the request details (name, provider, expiry) from being complete. */
  function initializeBlocker(): string | null {
    if (wizard.initializeForm.name.trim().length === 0) {
      return 'Give the request a name so recipients recognise it in their inbox.';
    }
    if (initializeNameError.value) {
      return initializeNameError.value;
    }
    if (!wizard.initializeForm.provider) {
      return 'Pick a signing provider before continuing.';
    }
    return initializeExpirationError.value;
  }

  /**
   * The sentence the blocker banner shows. Derived from the same conditions as
   * the Continue gate, so the two can never disagree.
   */
  const activeStepBlocker = computed<string | null>(() => {
    switch (activeStep.value) {
      case 'initialize':
        return initializeBlocker();
      case 'upload': {
        if (!hasUploadConfiguration.value) {
          return 'Select at least one file and mark it as a signable document.';
        }
        return initializeBlocker();
      }
      case 'signees': {
        if (normalizedSignees.value.participants.length === 0) {
          return 'Add at least one recipient from your contractual parties.';
        }
        const incomplete = wizard.signeesForm.find(signee => !isSigneeReadyForSubmission(signee));
        if (incomplete) {
          const signer = signeeSigner(incomplete);
          if (signer && !allows(signer.permissions, ACCESS_PERMISSION_WRITE)) {
            return `${signer.name} is missing a signing email or a usable first and last name, and you can only view them — ask someone with edit access to fix the party.`;
          }
          return 'One recipient is missing a signer, a signing email or a usable first and last name — fix the party first.';
        }
        return null;
      }
      case 'review': {
        const missingSigners = signerPartiesWithoutSignatureField.value;
        if (missingSigners.length > 0) {
          return `${describeParties(missingSigners)} ${
            missingSigners.length > 1 ? 'have' : 'has'
          } no signature field yet.`;
        }
        const missingDocuments = signableDocumentsWithoutSignatureField.value;
        if (missingDocuments.length > 0) {
          return `${missingDocuments.length} signable document${
            missingDocuments.length > 1 ? 's' : ''
          } still need a signature field.`;
        }
        return reviewParties.value.some(party => party.role === SigningRequestSigneeRole.SIGNER)
          ? null
          : 'Add at least one signer before placing fields.';
      }
      case 'activate': {
        // canActivate is false while the activation itself is in flight; that
        // is not a blocker the user needs to resolve.
        if (submitting.value) return null;
        if (!allSignableDocumentsDetected.value) {
          return 'Still checking the documents for existing signatures.';
        }
        if (!allRequiredSignatureClearAcksGiven.value) {
          return 'Acknowledge that the existing signatures on these documents will be cleared.';
        }
        if (documentsWithSupersededRevision.value.length > 0) {
          return 'A document changed while you were preparing this request — resolve the warning below.';
        }
        if (fieldsOnUnpinnedRevision.value.length > 0) {
          return 'Some signature fields belong to an outdated document version — place them again on the review step.';
        }
        return canActivate.value ? null : 'Some earlier step is still incomplete.';
      }
    }

    // Unreachable: the switch covers every step key.
    return null;
  });

  const currentStepIndex = computed(() =>
    SIGNING_WIZARD_STEPS.findIndex(step => step.key === activeStep.value)
  );
  const activeStepDefinition = computed(() => SIGNING_WIZARD_STEPS[currentStepIndex.value]);
  const canGoBack = computed(() => currentStepIndex.value > 0 && !isAnyStepLoading.value);

  const continueLabel = computed(() => {
    if (activeStep.value === 'activate') {
      return submitting.value ? 'Sending…' : 'Send for signing';
    }
    return 'Continue';
  });

  const continueDisabled = computed(() => {
    if (!isStepAccessible(activeStep.value) || isAnyStepLoading.value) return true;

    switch (activeStep.value) {
      case 'initialize':
        return !hasInitializeConfiguration.value;
      case 'upload':
        return !hasUploadConfiguration.value || !hasInitializeConfiguration.value;
      case 'signees':
        return !hasSigneesConfiguration.value;
      case 'review':
        return !(
          allReviewSignersHaveSignatureField.value &&
          allReviewSignableDocumentsHaveSignatureField.value
        );
      case 'activate':
        return !canActivate.value;
    }

    // Unreachable: the switch covers every step key.
    return true;
  });

  function isStepComplete(stepKey: SignatureFlowStepKey): boolean {
    switch (stepKey) {
      case 'initialize':
        return wizard.steps.initialize.completed && hasInitializeConfiguration.value;
      case 'upload':
        return (
          wizard.steps.upload.completed &&
          hasUploadConfiguration.value &&
          isStepComplete('initialize')
        );
      case 'signees':
        return wizard.steps.signees.completed && hasSigneesConfiguration.value;
      case 'review':
        return wizard.steps.review.completed;
      case 'activate':
        return wizard.steps.activate.completed && Boolean(wizard.steps.activate.response);
    }
  }

  function isStepAccessible(stepKey: SignatureFlowStepKey): boolean {
    const index = SIGNING_WIZARD_STEPS.findIndex(step => step.key === stepKey);
    return SIGNING_WIZARD_STEPS.slice(0, index).every(step => isStepComplete(step.key));
  }

  /** The mono count the rail shows beside a reachable step. */
  function stepCount(stepKey: SignatureFlowStepKey): number | undefined {
    switch (stepKey) {
      case 'upload':
        return wizard.uploadForm.documents.length;
      case 'signees':
        return normalizedSignees.value.participants.length;
      case 'review':
        return wizard.reviewForm.fields.length;
      default:
        return undefined;
    }
  }

  // ── Step transitions ──────────────────────────────────────────────────────

  function selectStep(stepKey: SignatureFlowStepKey): void {
    if (!isStepAccessible(stepKey)) return;
    activeStep.value = stepKey;
  }

  function goBack(): void {
    if (!canGoBack.value) return;
    activeStep.value = SIGNING_WIZARD_STEPS[currentStepIndex.value - 1].key;
  }

  async function goNext(): Promise<void> {
    if (continueDisabled.value) return;

    switch (activeStep.value) {
      case 'initialize':
        completeStep('initialize');
        activeStep.value = 'upload';
        return;
      case 'upload':
        completeStep('initialize');
        syncReviewFormWithWorkflow();
        markDownstreamReviewDirty();
        completeStep('upload');
        activeStep.value = 'signees';
        return;
      case 'signees':
        syncReviewFormWithWorkflow();
        markDownstreamReviewDirty();
        completeStep('signees');
        activeStep.value = 'review';
        return;
      case 'review':
        completeStep('review');
        activeStep.value = 'activate';
        return;
      case 'activate':
        await activate();
    }
  }

  function completeStep(stepKey: 'initialize' | 'upload' | 'signees' | 'review'): void {
    clearStepFeedback(wizard.steps[stepKey]);
    applyCompletedStepState(stepKey, true);
  }

  // ── Activation ────────────────────────────────────────────────────────────

  async function activate(): Promise<void> {
    if (!canActivate.value) return;

    resetStep(wizard.steps.activate);
    wizard.steps.activate.loading = true;

    try {
      // The provider is sent what the draft holds, so everything typed must be
      // on the server first (a new request is created by this same flush).
      await flushSaves();
      const draftUid = wizard.signingRequestUid;
      if (saveState.value === 'error' || !draftUid) {
        throw new Error(saveError.value ?? 'The draft could not be saved.');
      }

      const response = await activateSignatureDraft(draftUid);
      const createdUid = response.signing_request_uid;
      // Off before the state changes below, so nothing tries to save a request that is out.
      persistenceReady.value = false;
      wizard.steps.activate.completed = true;
      wizard.steps.activate.response = response;
      message.success('Sent for signing.');

      // Land on the request just sent. Replace, so Back doesn't reopen a
      // wizard for a request that is already out. No reset: the wizard state
      // is per-view and unmounts with it — resetting in place is what dropped
      // users on a blank Initialize step whenever the navigation was aborted.
      // `sent` tells the detail page the hand-off to the provider is still running
      // in the background, so it watches for the request to leave draft.
      const target = { ...signingRequestRoute(createdUid), query: { sent: '1' } };
      const failure = await router.replace(target);
      if (isNavigationFailure(failure)) {
        window.location.assign(router.resolve(target).href);
      }
    } catch (error) {
      const detail = extractErrorDetail(
        error,
        'Something went wrong while sending the signature request.'
      );
      wizard.steps.activate.error = detail;
      message.error(`${detail} Please contact GAIA Support if the problem persists.`);
    } finally {
      wizard.steps.activate.loading = false;
    }
  }

  // ── Navigation out ────────────────────────────────────────────────────────

  function navigateBack(): void {
    if (window.opener) {
      window.close();
      return;
    }

    const editingUid = editingSigningRequestUid.value;
    if (editingUid) {
      void router.push(signingRequestRoute(editingUid));
      return;
    }

    if (launchedFromOverview.value) {
      void router.push({
        name: 'signing-requests',
        query: contractUid.value ? { contractUid: contractUid.value } : {},
      });
      return;
    }

    if (contractUid.value) {
      void router.push(contractRoute(contractUid.value));
      return;
    }

    void router.push({ name: 'signing-requests' });
  }

  // ── Form updates ──────────────────────────────────────────────────────────

  function updateInitializeForm(value: SignatureInitializeForm): void {
    wizard.initializeForm = normalizeInitializeForm(value);
    clearStepFeedback(wizard.steps.initialize);
  }

  /**
   * The request is named after what is signed, so a new one needs no typing:
   * the contract's name, else the first signable file's without its extension.
   * Only a name the wizard filled in itself follows the documents; one the
   * person typed (or a restored or edited request's) is theirs and stays.
   */
  let autoName: string | null = null;

  function suggestedRequestName(form: SignatureUploadForm): string {
    if (form.contract_selection?.contract_name) return form.contract_selection.contract_name;
    const document =
      form.documents.find(item => item.signing_file_type === SigningFileType.SIGNABLE_DOCUMENT) ??
      form.documents.at(0);
    const fileName = document?.vault_file.current_revision?.file_name ?? '';
    return fileName.replace(/\.[^./\\]+$/, '');
  }

  watch(
    () => wizard.uploadForm,
    form => {
      const current = wizard.initializeForm.name.trim();
      if (current.length > 0 && current !== autoName) return;
      const suggestion = suggestedRequestName(form);
      if (suggestion === current) return;
      autoName = suggestion;
      wizard.initializeForm = { ...wizard.initializeForm, name: suggestion };
    },
    { deep: true }
  );

  function updateUploadForm(value: SignatureUploadForm): void {
    wizard.uploadForm = value;
    clearStepFeedback(wizard.steps.upload);
    syncReviewFormWithWorkflow();
    markDownstreamReviewDirty();
  }

  function updateSigneesForm(value: SignatureSigneeForm[]): void {
    wizard.signeesForm = value;
    clearStepFeedback(wizard.steps.signees);
    syncReviewFormWithWorkflow();
    markDownstreamReviewDirty();
  }

  function updateReviewForm(value: SignatureReviewForm): void {
    wizard.reviewForm = value;
    clearStepFeedback(wizard.steps.review);
    wizard.steps.review.completed = false;
    wizard.steps.review.response = null;
    resetStep(wizard.steps.activate);
    wizard.activateForm.confirmed = false;
  }

  // ── Autosave ──────────────────────────────────────────────────────────────

  const saveState = ref<GaiaSaveStateValue>('saved');
  const saveError = ref<string | null>(null);
  const lastSavedAt = ref<Date | null>(null);
  /** What the server holds since the last save — each save sends only what differs. */
  let lastSaved: SignatureActivateRequest | null = null;
  let saveTimer: number | null = null;
  let saveInFlight: Promise<void> | null = null;

  /** Tour playback fills the wizard with demo values; those never become a draft. */
  function autosaveEnabled(): boolean {
    return (
      persistenceReady.value && !wizard.steps.activate.completed && !useTourStore().activeTourKey
    );
  }

  /**
   * A new request becomes a draft once its documents are settled — Continue
   * past step 1. Picking a file, or opening the wizard and leaving, creates
   * nothing; the documents are also what the request is named after.
   */
  const hasDraftContent = computed(
    () => wizard.steps.upload.completed && wizard.uploadForm.documents.length > 0
  );

  function hasUnsavedChanges(): boolean {
    if (!wizard.signingRequestUid) return hasDraftContent.value;
    return !lastSaved || draftSaveKey(activateRequestPreview.value) !== draftSaveKey(lastSaved);
  }

  function clearSaveTimer(): void {
    if (saveTimer !== null) {
      window.clearTimeout(saveTimer);
      saveTimer = null;
    }
  }

  function scheduleFlush(): void {
    clearSaveTimer();
    saveTimer = window.setTimeout(() => {
      saveTimer = null;
      void flushSaves();
    }, AUTOSAVE_DEBOUNCE_MS);
  }

  /**
   * Send what is pending now. Serialised: a flush that finds a save in flight
   * waits for it, then sends whatever changed meanwhile. Awaited by Send and by
   * the leave guard.
   */
  async function flushSaves(): Promise<void> {
    clearSaveTimer();
    if (saveInFlight) await saveInFlight;
    if (!autosaveEnabled() || !hasUnsavedChanges()) {
      if (saveState.value !== 'error') saveState.value = 'saved';
      return;
    }

    const payload = cloneAsJson(activateRequestPreview.value);
    saveState.value = 'saving';

    saveInFlight = (async (): Promise<void> => {
      try {
        let uid = wizard.signingRequestUid;
        if (!uid) {
          const created = await createSignatureDraft(payload.initialize);
          uid = created.uid;
          wizard.signingRequestUid = uid;
          // The create carried the request's own settings and nothing else.
          lastSaved = { ...payload, documents: [], participants: { participants: [] }, fields: [] };
          // The URL becomes the draft's own, so a refresh or a bookmark comes back to it.
          // Same component, so the wizard stays mounted and keeps its state.
          // The query rides along: the prefill it carries is part of the request's context.
          await router.replace({ ...signingRequestEditRoute(uid), query: route.query });
        }

        const persistedUids = await saveSignatureDraft(uid, payload, lastSaved);
        // Fields learn their server uid, so the next save updates rather than recreates.
        const withUids = (fields: SignatureDraftField[]): SignatureDraftField[] =>
          fields.map(field => {
            const persisted = persistedUids.get(field.uid);
            return persisted && persisted !== field.persisted_uid
              ? { ...field, persisted_uid: persisted }
              : field;
          });
        wizard.reviewForm = { ...wizard.reviewForm, fields: withUids(wizard.reviewForm.fields) };
        lastSaved = { ...payload, fields: withUids(payload.fields) };
        lastSavedAt.value = new Date();
        saveError.value = null;

        // Edits that landed during the request go out next.
        if (hasUnsavedChanges()) {
          saveState.value = 'pending';
          scheduleFlush();
        } else {
          saveState.value = 'saved';
        }
      } catch (error) {
        // Retried with everything on "Save now" or the next edit; `lastSaved` is
        // unchanged, so nothing that failed is assumed to be on the server.
        saveError.value = extractErrorDetail(error, 'The draft could not be saved.');
        saveState.value = 'error';
        logger.error('Signature draft autosave failed:', error);
      } finally {
        saveInFlight = null;
      }
    })();

    await saveInFlight;
  }

  watch(
    activateRequestPreview,
    () => {
      if (!autosaveEnabled() || !hasUnsavedChanges()) return;
      saveState.value = 'pending';
      scheduleFlush();
    },
    { deep: true }
  );

  // Continuing to step 2 changes nothing that is saved, so the edit watcher
  // above can't see it — the draft is created here, straight away.
  watch(hasDraftContent, ready => {
    if (ready && !wizard.signingRequestUid && autosaveEnabled()) void flushSaves();
  });

  // ── Wizard lifecycle ──────────────────────────────────────────────────────

  function resetWizard(): void {
    wizard.signingRequestUid = null;
    wizard.initializeForm = cloneAsJson(defaultInitializeForm);
    wizard.uploadForm = { documents: [], contract_selection: null };
    wizard.signeesForm = [];
    wizard.reviewForm = createDefaultReviewForm();
    wizard.activateForm = { confirmed: false };
    signatureClearAcknowledgements.value = {};
    for (const step of Object.values(wizard.steps)) {
      resetStep(step);
    }
    activeStep.value = 'upload';
    autoName = null;
    clearSaveTimer();
    lastSaved = null;
    saveState.value = 'saved';
    saveError.value = null;
    lastSavedAt.value = null;
  }

  async function initializeFreshWizard(): Promise<void> {
    persistenceReady.value = false;
    resetWizard();

    try {
      const organisation = await organizationStore.fetchActiveOrganization();
      // A new request expires after the organisation's default; the author can still change it.
      const days = organisation?.signing_expiration_days;
      if (days) {
        wizard.initializeForm.expiration_date = toDatetimeLocal(
          endOfLocalDay(expiryDayInDays(days))
        );
      }

      const prefilledUploadForm = await buildPrefilledUploadFormFromRouteQuery();
      if (prefilledUploadForm) {
        wizard.uploadForm = prefilledUploadForm;
        syncReviewFormWithWorkflow();
      }
    } catch (error) {
      message.error(extractErrorDetail(error, 'Failed to prefill the requested signature files.'));
    } finally {
      persistenceReady.value = true;
      applyStepFromRoute();
    }
  }

  /**
   * Load an existing draft, placed fields included. The first three steps are
   * marked complete so the user lands where input is still needed.
   */
  async function hydrateFromSigningRequest(signingRequestUid: string): Promise<void> {
    persistenceReady.value = false;
    resetWizard();

    try {
      await organizationStore.fetchActiveOrganization();

      const signingRequest = await SignatureRequestService.get(signingRequestUid);
      if (signingRequest.status !== SigningRequestStatus.DRAFT) {
        message.warning('Only draft signature requests can be edited.');
        await router.replace(signingRequestRoute(signingRequestUid));
        return;
      }

      const [uploadForm, participants] = await Promise.all([
        buildUploadFormFromSigningRequest(signingRequestUid),
        fetchParticipantsForSigningRequest(signingRequestUid),
      ]);
      const draftFields = await buildDraftFieldsFromSigningRequest(signingRequestUid, participants);

      wizard.signingRequestUid = signingRequestUid;
      wizard.initializeForm = normalizeInitializeForm({
        name: signingRequest.name ?? '',
        provider: isEnumValue(SigningProvider, signingRequest.provider)
          ? signingRequest.provider
          : undefined,
        message: signingRequest.message ?? '',
        delivery_mode: signingRequest.delivery_mode,
        ordered_approvers: signingRequest.ordered_approvers,
        ordered_signers: signingRequest.ordered_signers,
        expiration_date: signingRequest.expiration_date
          ? expiryDayFromIso(signingRequest.expiration_date)
          : '',
      });
      wizard.uploadForm = uploadForm;
      // A participant whose party was deleted comes back with no party; it can't be re-sent.
      wizard.signeesForm = participants
        .filter(participant => participant.contractual_party)
        .map(participant => ({
          party: participant.contractual_party ?? null,
          representative: participant.representative ?? null,
          role: participant.role,
          signature_type: participant.signature_type ?? SignatureType.ELECTRONIC_SIGNATURE,
        }));
      // Assign before syncing: the sync prunes fields whose recipient or
      // document is gone.
      wizard.reviewForm = { ...wizard.reviewForm, fields: draftFields };
      syncReviewFormWithWorkflow();

      applyCompletedStepState('initialize', true);
      applyCompletedStepState('upload', true);
      applyCompletedStepState('signees', true);
      applyCompletedStepState(
        'review',
        allReviewSignersHaveSignatureField.value &&
          allReviewSignableDocumentsHaveSignatureField.value
      );

      activeStep.value = resolveAccessibleStep(parseStepFromQuery() ?? 'activate');
      // What was just loaded is what the server holds — reopening a draft is not an edit.
      lastSaved = cloneAsJson(activateRequestPreview.value);
    } catch (error) {
      message.error(extractErrorDetail(error, 'Failed to load the signature request.'));
    } finally {
      persistenceReady.value = true;
    }
  }

  // ── Internal state helpers ────────────────────────────────────────────────

  function applyCompletedStepState(
    stepKey: 'initialize' | 'upload' | 'signees' | 'review',
    completed: boolean
  ): void {
    if (stepKey === 'initialize') {
      wizard.steps.initialize.completed = completed && hasInitializeConfiguration.value;
      wizard.steps.initialize.response = wizard.steps.initialize.completed
        ? { completed: true }
        : null;
      return;
    }

    if (stepKey === 'upload') {
      wizard.steps.upload.completed = completed && hasUploadConfiguration.value;
      wizard.steps.upload.response = wizard.steps.upload.completed
        ? { completed: true, document_count: wizard.uploadForm.documents.length }
        : null;
      return;
    }

    if (stepKey === 'signees') {
      wizard.steps.signees.completed = completed && hasSigneesConfiguration.value;
      wizard.steps.signees.response = wizard.steps.signees.completed
        ? { completed: true, party_count: normalizedSignees.value.participants.length }
        : null;
      return;
    }

    wizard.steps.review.completed = completed;
    wizard.steps.review.response = completed ? { acknowledged: true } : null;
  }

  function markDownstreamReviewDirty(): void {
    clearStepFeedback(wizard.steps.review);
    wizard.steps.review.completed = false;
    wizard.steps.review.response = null;
    resetStep(wizard.steps.activate);
    wizard.activateForm.confirmed = false;
  }

  /**
   * Drop placed fields whose recipient or document is gone, and keep the
   * active document/party/selection pointing at something that still exists.
   */
  function syncReviewFormWithWorkflow(): void {
    const signableDocumentUids = new Set(
      signableUploadDocuments.value.map(document => document.vault_file.uid)
    );
    const signableRevisionUids = new Set(
      signableUploadDocuments.value
        .map(document => document.vault_file.current_revision?.uid)
        .filter((uid): uid is string => typeof uid === 'string' && uid.length > 0)
    );
    const partyRoleByUid = new Map(reviewParties.value.map(party => [party.uid, party.role]));
    const allowedPartyUids = new Set(reviewParties.value.map(party => party.uid));

    const nextFields = wizard.reviewForm.fields.filter(field => {
      if (!field.recipient_id || !allowedPartyUids.has(field.recipient_id)) return false;
      if (
        !field.vault_file_revision_uid ||
        !signableRevisionUids.has(field.vault_file_revision_uid)
      )
        return false;

      return !(
        field.type === SignatureFieldType.SIGNATURE &&
        partyRoleByUid.get(field.recipient_id) !== SigningRequestSigneeRole.SIGNER
      );
    });

    const signerParty = reviewParties.value.find(
      party => party.role === SigningRequestSigneeRole.SIGNER
    );
    const defaultPartyUid = signerParty?.uid ?? reviewParties.value.at(0)?.uid ?? null;
    const defaultDocumentUid = signableUploadDocuments.value.at(0)?.vault_file.uid ?? null;
    const currentDocumentUid = wizard.reviewForm.active_document_vault_file_uid;
    const currentPartyUid = wizard.reviewForm.active_party_uid;
    const currentFieldUid = wizard.reviewForm.selected_field_uid;

    wizard.reviewForm = {
      ...wizard.reviewForm,
      fields: nextFields,
      active_document_vault_file_uid:
        currentDocumentUid && signableDocumentUids.has(currentDocumentUid)
          ? currentDocumentUid
          : defaultDocumentUid,
      active_party_uid:
        currentPartyUid && allowedPartyUids.has(currentPartyUid)
          ? currentPartyUid
          : defaultPartyUid,
      selected_field_uid:
        currentFieldUid && nextFields.some(field => field.uid === currentFieldUid)
          ? currentFieldUid
          : null,
    };
  }

  function resolveAccessibleStep(preferredStep: SignatureFlowStepKey): SignatureFlowStepKey {
    if (isStepAccessible(preferredStep)) return preferredStep;

    const accessible = SIGNING_WIZARD_STEPS.map(step => step.key).filter(stepKey =>
      isStepAccessible(stepKey)
    );

    return accessible.at(-1) ?? 'upload';
  }

  function parseStepFromQuery(): SignatureFlowStepKey | null {
    const raw = route.query.step;
    const value = Array.isArray(raw) ? raw[0] : raw;
    if (typeof value !== 'string') return null;

    return SIGNING_WIZARD_STEPS.find(step => step.key === value)?.key ?? null;
  }

  /** Lands on the step the URL asks for, clamped to what the state allows. */
  function applyStepFromRoute(): void {
    const requestedStep = parseStepFromQuery();
    if (!requestedStep) return;
    activeStep.value = resolveAccessibleStep(requestedStep);
  }

  // ── Prefill from the route query ──────────────────────────────────────────

  async function buildPrefilledUploadFormFromRouteQuery(): Promise<SignatureUploadForm | null> {
    if (prefillMode.value === 'contract' && prefillContractUid.value) {
      return buildPrefilledUploadFormFromContractUid(prefillContractUid.value);
    }
    if (prefillMode.value === 'contract-files' && prefillContractFileUids.value.length > 0) {
      return buildPrefilledUploadFormFromContractFileUids(prefillContractFileUids.value);
    }
    if (prefillMode.value === 'vault-files' && prefillVaultFileUids.value.length > 0) {
      return {
        documents: normalizeUploadDocuments(
          await Promise.all(
            prefillVaultFileUids.value.map(vaultFileUid =>
              buildDocumentFromVaultFileUid(vaultFileUid)
            )
          )
        ),
        contract_selection: null,
      };
    }

    return null;
  }

  async function buildPrefilledUploadFormFromContractUid(
    requestedContractUid: string
  ): Promise<SignatureUploadForm> {
    const [contract, allContractFiles] = await Promise.all([
      ContractsService.getById(requestedContractUid),
      fetchAllContractFiles(requestedContractUid),
    ]);
    // Drafts stay behind: the backend only takes finished documents.
    const contractFiles = allContractFiles.filter(file => !isDraftContractFile(file));
    if (contractFiles.length === 0 && allContractFiles.length > 0) {
      throw new Error(DRAFT_NOT_SIGNABLE);
    }

    const documents = normalizeUploadDocuments(
      await Promise.all(
        contractFiles.map(contractFile =>
          buildDocumentFromContractFile(contractFile, { uid: contract.uid, name: contract.name })
        )
      )
    );

    return {
      documents,
      contract_selection: buildContractSelectionState(documents, 'full-contract'),
    };
  }

  async function buildPrefilledUploadFormFromContractFileUids(
    requestedContractFileUids: string[]
  ): Promise<SignatureUploadForm> {
    const contractFiles = await Promise.all(
      requestedContractFileUids.map(contractFileUid =>
        ContractFileDetailService.getContractFile(contractFileUid)
      )
    );
    if (contractFiles.some(isDraftContractFile)) throw new Error(DRAFT_NOT_SIGNABLE);

    let resolvedContractUid = contractUid.value;
    let resolvedContractName: string | null = null;

    for (const contractFile of contractFiles) {
      const fileContractUid = contractFile.contract.uid;

      resolvedContractUid ??= fileContractUid;

      if (fileContractUid !== resolvedContractUid) {
        throw new Error('All prefilled contract files must belong to the same contract.');
      }

      resolvedContractName ??= contractFile.contract.name;
    }

    if (!resolvedContractUid) {
      throw new Error('Could not resolve the parent contract for the requested contract file.');
    }

    if (!resolvedContractName) {
      const contract = await ContractsService.getById(resolvedContractUid);
      resolvedContractName = contract.name;
    }

    const contractMeta = { uid: resolvedContractUid, name: resolvedContractName };
    const documents = normalizeUploadDocuments(
      await Promise.all(
        contractFiles.map(contractFile => buildDocumentFromContractFile(contractFile, contractMeta))
      )
    );

    return {
      documents,
      contract_selection: buildContractSelectionState(documents, 'contract-files'),
    };
  }

  // ── Edit-mode hydration helpers ───────────────────────────────────────────

  async function buildUploadFormFromSigningRequest(
    signingRequestUid: string
  ): Promise<SignatureUploadForm> {
    const response = await getDocuments({ path: { signing_request_uid: signingRequestUid } });
    if (response.error) {
      throw new Error(
        extractErrorDetail(response.error, "Failed to load the signature request's documents.")
      );
    }

    // Pin each document to the revision the request holds, not to whatever is
    // newest on the file today: the fields it carries were placed on that
    // revision, and a newer one would strand them.
    const documents = response.data.map<SignatureUploadDocumentForm>(document => ({
      vault_file: { ...document.vault_file, current_revision: document.vault_file_revision },
      signing_file_type: document.signing_file_type,
      effective_file: buildEffectiveFileFromRevision(document.vault_file_revision),
      source: { kind: 'vault-file' },
    }));

    return { documents, contract_selection: null };
  }

  async function fetchParticipantsForSigningRequest(
    signingRequestUid: string
  ): Promise<ParticipantInDb[]> {
    const response = await getParticipants({ path: { signing_request_uid: signingRequestUid } });
    if (response.error) {
      throw new Error(extractErrorDetail(response.error, 'Failed to load signing participants.'));
    }

    return response.data;
  }

  /**
   * Draft fields address their recipient by *party* uid (what `reviewParties`
   * exposes), so each field's `participant_uid` is translated back.
   */
  async function buildDraftFieldsFromSigningRequest(
    signingRequestUid: string,
    participants: ParticipantInDb[]
  ): Promise<SignatureDraftField[]> {
    const entityUidByParticipantUid = new Map(
      participants.flatMap(participant => {
        const partyUid = participant.contractual_party?.uid;
        return partyUid ? [[participant.uid, partyUid] as const] : [];
      })
    );

    const fieldsPerParticipant = await Promise.all(
      participants.map(async participant => {
        const response = await getFields({
          path: { signing_request_uid: signingRequestUid, participant_uid: participant.uid },
        });
        if (response.error) {
          throw new Error(extractErrorDetail(response.error, 'Failed to load signature fields.'));
        }
        return response.data;
      })
    );

    return fieldsPerParticipant.flat().map(field => ({
      uid: field.uid,
      persisted_uid: field.uid,
      page: field.page,
      x: field.x,
      y: field.y,
      width: field.width,
      height: field.height,
      recipient_id: entityUidByParticipantUid.get(field.participant_uid) ?? null,
      vault_file_revision_uid: field.vault_file_revision_uid ?? null,
      type: field.type,
      reason: field.reason ?? null,
      font_size: field.font_size ?? undefined,
    }));
  }

  // ── Signature-detection refresh ───────────────────────────────────────────

  // Point a document at `revision`, preview data included. Only ever called
  // for the revision the request is already pinned to (a status refresh) or on
  // the user's explicit switch-over.
  function pinDocumentRevision(fileUid: string, revision: VaultFileRevisionInDb): void {
    const index = wizard.uploadForm.documents.findIndex(doc => doc.vault_file.uid === fileUid);
    if (index === -1) return;

    const existing = wizard.uploadForm.documents[index];
    const nextDocuments = wizard.uploadForm.documents.slice();
    nextDocuments[index] = {
      ...existing,
      vault_file: { ...existing.vault_file, current_revision: revision },
      effective_file: buildEffectiveFileFromRevision(revision),
    };
    wizard.uploadForm = { ...wizard.uploadForm, documents: nextDocuments };
  }

  // A fresh look at a signable document's file, from the initial sync or a
  // live `vault_file_updated` event. Merging is limited to the revision the
  // request is pinned to, so detection status keeps flowing in; a *different*
  // revision means the file changed underneath the placed fields, and is
  // parked for the activate step to surface instead of being adopted.
  function applyRevisionUpdate(
    fileUid: string,
    revision: VaultFileRevisionInDb | null | undefined
  ): void {
    if (!revision) return;

    const document = wizard.uploadForm.documents.find(doc => doc.vault_file.uid === fileUid);
    if (!document) return;

    const pinnedRevisionUid = document.vault_file.current_revision?.uid;
    if (pinnedRevisionUid && revision.uid !== pinnedRevisionUid) {
      supersededRevisions.value = { ...supersededRevisions.value, [fileUid]: revision };
      return;
    }

    pinDocumentRevision(fileUid, revision);
  }

  // Take a fresh look at every signable document: detection may have finished
  // while we were not subscribed, and the file may have gained a revision this
  // request is not pinned to. Both answers come from the same metadata call,
  // and `supersededRevisions` does not survive a reload, so this runs for
  // every document rather than only for those still awaiting detection.
  async function refreshSignableDocuments(): Promise<void> {
    const documents = signableUploadDocuments.value;
    if (documents.length === 0) return;

    const refreshed = await Promise.allSettled(
      documents.map(async document => {
        const response = await getVaultFileMetadata({
          path: { file_uid: document.vault_file.uid },
        });
        return { uid: document.vault_file.uid, refreshed: response.data };
      })
    );

    for (const result of refreshed) {
      if (result.status !== 'fulfilled' || !result.value.refreshed) continue;
      applyRevisionUpdate(result.value.uid, result.value.refreshed.current_revision);
    }
  }

  // The user chose to move a document to the newer revision. The fields they
  // placed belong to the revision being left behind and cannot be carried over
  // — page geometry may differ — so the sync drops them and the review step
  // reopens for re-placement.
  function adoptDocumentRevision(fileUid: string): void {
    const revision = supersededRevisions.value[fileUid];
    if (!revision) return;

    pinDocumentRevision(fileUid, revision);

    const next = { ...supersededRevisions.value };
    delete next[fileUid];
    supersededRevisions.value = next;

    syncReviewFormWithWorkflow();
    markDownstreamReviewDirty();
    activeStep.value = 'review';
  }

  // Stale acknowledgements would silently unlock activation for a document the
  // user swapped out, so they are pruned whenever the document set changes.
  watch(
    () => signableDocumentRevisions.value.map(revision => revision.uid),
    revisionUids => {
      const alive = new Set(revisionUids);
      const next: Record<string, boolean> = {};
      for (const [key, value] of Object.entries(signatureClearAcknowledgements.value)) {
        if (alive.has(key) && value) {
          next[key] = true;
        }
      }
      signatureClearAcknowledgements.value = next;
    }
  );

  // A parked newer revision only matters while its document is still part of
  // the request; dropping the document drops the warning with it.
  watch(
    () => signableUploadDocuments.value.map(document => document.vault_file.uid),
    fileUids => {
      const alive = new Set(fileUids);
      const stale = Object.keys(supersededRevisions.value).filter(fileUid => !alive.has(fileUid));
      if (stale.length === 0) return;

      supersededRevisions.value = Object.fromEntries(
        Object.entries(supersededRevisions.value).filter(([fileUid]) => alive.has(fileUid))
      );
    }
  );

  // On entering the activate step, sync once against the server; later changes
  // arrive via the websocket below.
  watch(
    activeStep,
    step => {
      if (step === 'activate') {
        void refreshSignableDocuments();
      }
    },
    { immediate: true }
  );

  useVaultWebSocket({
    fileUids: () =>
      activeStep.value === 'activate'
        ? signableUploadDocuments.value.map(document => document.vault_file.uid)
        : [],
    onFileUpdated: (uid, fileData) => {
      applyRevisionUpdate(
        uid,
        (fileData as unknown as { current_revision?: VaultFileRevisionInDb }).current_revision
      );
    },
  });

  // ── Boot + URL sync ───────────────────────────────────────────────────────

  onMounted(() => {
    const editingUid = editingSigningRequestUid.value;
    if (editingUid) {
      void hydrateFromSigningRequest(editingUid);
      return;
    }

    void initializeFreshWizard();
  });

  // The retired local draft cache left its snapshots behind; drafts are on the server now.
  try {
    const legacyKeys = Object.keys(window.localStorage).filter(key =>
      key.startsWith(LEGACY_DRAFT_KEY_PREFIX)
    );
    for (const key of legacyKeys) window.localStorage.removeItem(key);
  } catch {
    // Storage unavailable: nothing to clean up.
  }

  // Mirror the active step into `?step=` so refresh/back returns to it. The
  // route-name guard avoids writing while navigating away (e.g. after
  // activation).
  // The first save moves a new request onto the edit route; both are this wizard.
  const wizardRouteNames = new Set([route.name, 'signing-request-edit']);
  watch(activeStep, step => {
    if (!wizardRouteNames.has(route.name)) return;

    const query: LocationQueryRaw = { ...route.query };
    if (step === 'upload') {
      delete query.step;
    } else {
      query.step = step;
    }
    void router.replace({ query });
  });

  return {
    wizard,
    activeStep,
    activeStepDefinition,
    isEditMode,
    contractUid,
    reviewParties,
    activateRequestPreview,
    signatureClearAcknowledgements,
    revisionsRequiringClearAck,
    documentsWithSupersededRevision,
    adoptDocumentRevision,
    allSignableDocumentsDetected,
    signableUploadDocuments,
    initializeNameError,
    initializeExpirationError,
    saveState,
    saveError,
    lastSavedAt,
    flushSaves,
    submitting,
    continueLabel,
    continueDisabled,
    canGoBack,
    activeStepBlocker,
    isStepAccessible,
    isStepComplete,
    stepCount,
    selectStep,
    goBack,
    goNext,
    navigateBack,
    updateInitializeForm,
    updateUploadForm,
    updateSigneesForm,
    updateReviewForm,
  };
}

// ── Pure helpers ────────────────────────────────────────────────────────────

function createStepState<TResponse>(): SignatureStepState<TResponse> {
  return { completed: false, loading: false, error: null, success: null, response: null };
}

/**
 * `unknown` rather than a generic: both helpers only ever null the response
 * out, and the callers reach the steps through a key union whose members carry
 * different response types.
 */
function resetStep(step: SignatureStepState<unknown>): void {
  step.completed = false;
  step.loading = false;
  step.error = null;
  step.success = null;
  step.response = null;
}

function clearStepFeedback(step: SignatureStepState<unknown>): void {
  step.error = null;
  step.success = null;
}

function createDefaultInitializeForm(): SignatureInitializeForm {
  const defaultExpiration = endOfLocalDay(Date.now() + 7 * 24 * 60 * 60 * 1000);

  return {
    name: '',
    provider: undefined,
    message: '',
    delivery_mode: FIXED_SIGNATURE_DELIVERY_MODE,
    ordered_approvers: false,
    ordered_signers: false,
    expiration_date: toDatetimeLocal(defaultExpiration),
  };
}

function normalizeInitializeForm(form: SignatureInitializeForm): SignatureInitializeForm {
  return { ...form, delivery_mode: FIXED_SIGNATURE_DELIVERY_MODE };
}

function createDefaultReviewForm(): SignatureReviewForm {
  return {
    fields: [],
    active_document_vault_file_uid: null,
    active_party_uid: null,
    selected_field_uid: null,
  };
}

function cloneAsJson<T>(value: T): T {
  return JSON.parse(JSON.stringify(value)) as T;
}

function queryListValue(
  ...values: (LocationQueryValue | LocationQueryValue[] | undefined)[]
): string[] {
  const normalized: string[] = [];

  for (const value of values) {
    if (Array.isArray(value)) {
      for (const item of value) {
        if (typeof item === 'string') normalized.push(...item.split(','));
      }
      continue;
    }
    if (typeof value === 'string') normalized.push(...value.split(','));
  }

  return [...new Set(normalized.map(entry => entry.trim()).filter(entry => entry.length > 0))];
}

function firstQueryValue(
  ...values: (LocationQueryValue | LocationQueryValue[] | undefined)[]
): string | null {
  return queryListValue(...values).at(0) ?? null;
}

function singleUid(values: string[]): string | null {
  return values.length === 1 ? values[0] : null;
}

function describeParties(parties: SignatureReviewParty[]): string {
  return parties.map(party => party.name || 'Unnamed signer').join(', ');
}

function isContractFileDocument(
  document: SignatureUploadDocumentForm
): document is SignatureUploadDocumentForm & {
  source: {
    kind: 'contract-file';
    contract_uid: string;
    contract_name: string | null;
    contract_file_uid: string;
  };
} {
  return document.source?.kind === 'contract-file';
}

/**
 * One entry per vault file. A file reachable both as a contract file and as a
 * plain vault file keeps its contract-file identity, so the row can still link
 * back to the contract.
 */
function normalizeUploadDocuments(
  documents: SignatureUploadDocumentForm[]
): SignatureUploadDocumentForm[] {
  const byVaultFileUid = new Map<string, SignatureUploadDocumentForm>();

  for (const document of documents) {
    const existing = byVaultFileUid.get(document.vault_file.uid);
    if (!existing || (isContractFileDocument(document) && !isContractFileDocument(existing))) {
      byVaultFileUid.set(document.vault_file.uid, document);
    }
  }

  return [...byVaultFileUid.values()];
}

function buildContractSelectionState(
  documents: SignatureUploadDocumentForm[],
  mode: SignatureUploadContractSelectionState['mode']
): SignatureUploadContractSelectionState | null {
  const contractDocuments = documents.filter(isContractFileDocument);
  if (contractDocuments.length === 0) return null;

  const contractUids = new Set(contractDocuments.map(document => document.source.contract_uid));
  if (contractUids.size !== 1) return null;

  const firstSource = contractDocuments[0].source;

  return {
    contract_uid: firstSource.contract_uid,
    contract_name: firstSource.contract_name,
    contract_file_uids: contractDocuments.map(document => document.source.contract_file_uid),
    mode,
  };
}

export async function buildDocumentFromVaultFileUid(
  vaultFileUid: string
): Promise<SignatureUploadDocumentForm> {
  const response = await getVaultFileMetadata({ path: { file_uid: vaultFileUid } });
  const vaultFile = response.data;

  if (!vaultFile?.current_revision) {
    throw new Error(`Vault file ${vaultFileUid} has no current revision.`);
  }

  return {
    vault_file: vaultFile,
    signing_file_type: SigningFileType.SIGNABLE_DOCUMENT,
    effective_file: buildEffectiveFileFromRevision(vaultFile.current_revision),
    source: { kind: 'vault-file' },
  };
}

const DRAFT_NOT_SIGNABLE = 'Finish the draft before sending it for signing.';

/**
 * A draft is rendered when viewed and never gets the stored preview signing
 * needs, so the backend refuses to add it to a request until it is finished.
 */
function isDraftContractFile(file: ContractManagementContractFileInDb): boolean {
  return file.vault_file?.current_revision?.kind === VaultFileRevisionKind.TEMPLATED;
}

export async function buildDocumentFromContractFile(
  contractFile: ContractManagementContractFileInDb,
  contractMeta: { uid: string; name: string | null }
): Promise<SignatureUploadDocumentForm> {
  const vaultFileUid = contractFile.vault_file?.uid;
  if (!vaultFileUid) {
    throw new Error(`Contract file ${contractFile.uid} is not linked to a vault file.`);
  }

  const response = await getVaultFileMetadata({ path: { file_uid: vaultFileUid } });
  const vaultFile = response.data;

  if (!vaultFile?.current_revision) {
    throw new Error(`Contract file ${contractFile.uid} has no current vault file revision.`);
  }

  return {
    vault_file: vaultFile,
    signing_file_type: SigningFileType.SIGNABLE_DOCUMENT,
    effective_file: buildEffectiveFileFromRevision(vaultFile.current_revision),
    source: {
      kind: 'contract-file',
      contract_uid: contractMeta.uid,
      contract_name: contractMeta.name,
      contract_file_uid: contractFile.uid,
      contract_file_description: contractFile.description ?? null,
      contract_file_type: contractFile.type,
    },
  };
}

async function fetchAllContractFiles(
  requestedContractUid: string
): Promise<ContractManagementContractFileInDb[]> {
  const pageSize = 50;
  const files: ContractManagementContractFileInDb[] = [];
  let page = 1;
  let totalCount = 0;

  while (page === 1 || files.length < totalCount) {
    const result = await ContractFilesService.getContractFilesForContract(requestedContractUid, {
      text: '',
      page,
      pageSize,
      sortKey: 'created_at',
      sortDirection: 'desc',
    });

    if (page === 1) {
      totalCount = result.result_count;
    }

    if (result.results.length === 0) break;

    files.push(...result.results);

    if (result.results.length < pageSize) break;

    page += 1;
  }

  return files;
}

function buildInitializePayload(
  form: SignatureInitializeForm,
  context: SigningRequestContractContext
): SignatureInitializeRequest {
  const expirationDate = form.expiration_date ? new Date(form.expiration_date) : null;
  const isValidExpiration = expirationDate !== null && !Number.isNaN(expirationDate.getTime());

  return {
    name: form.name.trim(),
    // Checked before the step can be submitted; the payload is also built as a live preview.
    provider: form.provider as SigningProvider,
    message: form.message.trim() || null,
    delivery_mode: FIXED_SIGNATURE_DELIVERY_MODE,
    ordered_approvers: form.ordered_approvers,
    ordered_signers: form.ordered_signers,
    expiration_date: isValidExpiration ? expiryDayToIso(expirationDate.getTime()) : null,
    ...(context.contract_uid ? { contract_uid: context.contract_uid } : {}),
    ...(context.contract_file_uid ? { contract_file_uid: context.contract_file_uid } : {}),
  };
}

function buildUploadPayload(form: SignatureUploadForm): SignatureUploadRequest {
  return form.documents.map(document => {
    const vaultFileRevision = document.vault_file.current_revision;
    if (!vaultFileRevision) {
      throw new Error(`Vault file ${document.vault_file.uid} is missing a current revision.`);
    }

    return {
      vault_file_revision: vaultFileRevision,
      signing_file_type: document.signing_file_type,
    };
  });
}

function buildSigneesPayload(form: SignatureSigneeForm[]): SignatureAddSigneesRequest {
  return {
    participants: form
      .filter(signee => isSigneeReadyForSubmission(signee))
      .map(signee => mapSigneeToParticipant(signee)),
  };
}

function buildReviewParties(form: SignatureSigneeForm[]): SignatureReviewParty[] {
  return form.flatMap(signee =>
    signee.party
      ? [
          {
            uid: signee.party.uid,
            role: signee.role,
            name: signeeName(signee),
            signer: signeeSigner(signee),
          },
        ]
      : []
  );
}

/** "Doruk Arslan", or "Doruk Arslan (Google)" when a representative signs for a legal entity. */
export function signeeName(signee: SignatureSigneeForm): string {
  const partyName = signee.party?.name.trim() ?? '';
  const signer = signee.representative?.name.trim() ?? '';
  if (signee.party?.kind === 'legal_entity' && signer) return `${signer} (${partyName})`;
  return partyName;
}

/**
 * The natural person who actually signs: the party itself, or a legal
 * entity's chosen representative. Null while a legal entity has none picked.
 */
export function signeeSigner(signee: SignatureSigneeForm): {
  name: string;
  email?: string | null;
  first_name?: string | null;
  last_name?: string | null;
  permissions?: number | null;
} | null {
  if (!signee.party) return null;
  return signee.party.kind === 'natural_person' ? signee.party : signee.representative;
}

/**
 * The provider needs an email plus a first and last name it accepts, and the
 * backend reads them off the signer's own party record — so a signer missing
 * either can't be submitted until that party is fixed.
 */
export function isSigneeReadyForSubmission(signee: SignatureSigneeForm): boolean {
  const signer = signeeSigner(signee);
  if (!signer) return false;

  const firstName = signer.first_name?.trim() ?? '';
  const lastName = signer.last_name?.trim() ?? '';
  return (
    Boolean(signer.email?.trim()) &&
    firstName.length > 0 &&
    isSignatureParticipantNameValid(firstName) &&
    lastName.length > 0 &&
    isSignatureParticipantNameValid(lastName)
  );
}

/** References only — the backend reads contact data from the stored parties. */
function mapSigneeToParticipant(signee: SignatureSigneeForm): ParticipantCreate {
  if (!signee.party) throw new Error('Signee is missing a contractual party.');
  return {
    contractual_party_uid: signee.party.uid,
    representative_uid:
      signee.party.kind === 'legal_entity' ? (signee.representative?.uid ?? null) : null,
    role: signee.role,
    signature_type: signee.signature_type,
  };
}
